import { Compass, Eye } from "lucide-react";
import React from "react";

interface InsightSectionProps {
  adventureBg?: string;
}

export const InsightSection: React.FC<InsightSectionProps> = ({ adventureBg }) => {
  return (
    <section className="py-32 px-6 relative bg-zinc-950">
      <div className="container mx-auto max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-5xl font-heading font-bold mb-8 leading-tight text-white">
              Social Intuition is <br />
              <span className="text-primary">A Superpower.</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-6 leading-relaxed">
              We all have blindspots. Moments where our intention doesn't match our impact. FlirtMachine helps you
              bridge that gap.
            </p>
            <p className="text-zinc-400 text-lg mb-10 leading-relaxed">
              True confidence comes from{" "}
              <span className="text-white font-bold">awareness</span>. Knowing exactly how your presence shifts the
              energy of a room, and having the wisdom to navigate it with grace.
            </p>

            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-zinc-900 border border-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Eye className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Clarity of Perception</h3>
                  <p className="text-zinc-500 text-sm">See the subtext you might be missing.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-zinc-900 border border-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Compass className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Effective Navigation</h3>
                  <p className="text-zinc-500 text-sm">
                    Move through social dynamics without losing yourself.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative h-[600px] w-full rounded-2xl overflow-hidden border border-white/5 shadow-2xl">
            {adventureBg && (
              <>
                <img
                  src={adventureBg}
                  alt="Data visualization of social dynamics"
                  className="w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-10">
                  <h3 className="text-2xl font-heading font-bold text-white mb-2">
                    Decode the Invisible
                  </h3>
                  <p className="text-zinc-400 font-light leading-relaxed">
                    Every interaction has a hidden layer. Subtext, micro-expressions, timing. FlirtMachine visualizes
                    these dynamics, turning intuition into actionable data.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
